import {
  BiCardView,
  BiDashboardLayout,
  BiDashboardPermission,
  BiDashboardView,
  BiDashboardVisibility,
  BiLayoutMode,
} from '@euler/model/bi/dashboard';
import { BiDraftResult } from '@euler/model/bi/draft';
import {
  BiDatasetRefKind,
  BiMeasureKind,
  BiQuerySpec,
  BiRelativeTimePreset,
  BiSortDirection,
  BiTimeGrain,
  BiTimeRangeKind,
} from '@euler/model/bi/query';
import { BiPalette, BiVizSpec, BiVizType } from '@euler/model/bi/viz';
import { kBiDraftFixtures } from '@euler/playground/bi-visualization/biDraftFixtures';
import { biFixtureQuery } from './biCatalogFixtures';

const at = '2026-10-17T06:30:00.000Z';

const orders = { kind: BiDatasetRefKind.Sql as const, model: 'orders' };
const settlement = {
  kind: BiDatasetRefKind.Sql as const,
  model: 'settlement_items',
};
const amount = { kind: BiMeasureKind.Field as const, field: 'amount' };
const items = { kind: BiMeasureKind.Field as const, field: 'item_count' };
const count = { kind: BiMeasureKind.Field as const, field: 'order_count' };
const recent = {
  kind: BiTimeRangeKind.Relative as const,
  preset: BiRelativeTimePreset.Last30Days,
};

/**
 * Specs over the built-in catalogs (a real response), so the editor opens
 * on real fields.
 */
const kSpecs: Record<string, BiQuerySpec> = {
  kpi: {
    dataset: settlement,
    measures: [amount],
    dimensions: [],
    filters: [],
    timeRange: {
      kind: BiTimeRangeKind.Relative,
      preset: BiRelativeTimePreset.ThisWeek,
    },
    sort: [],
  },
  line: {
    dataset: settlement,
    measures: [amount],
    dimensions: [{ field: 'created_at', grain: BiTimeGrain.Day }],
    filters: [],
    timeRange: recent,
    sort: [],
  },
  horizontal: {
    dataset: settlement,
    measures: [amount],
    dimensions: [{ field: 'name' }],
    filters: [],
    timeRange: recent,
    sort: [{ key: 'amount', dir: BiSortDirection.Desc }],
    limit: 5,
  },
  table: {
    dataset: settlement,
    measures: [items, amount],
    dimensions: [{ field: 'name' }],
    filters: [],
    timeRange: recent,
    sort: [{ key: 'item_count', dir: BiSortDirection.Desc }],
  },
  donut: {
    dataset: orders,
    measures: [count],
    dimensions: [{ field: 'service_type' }],
    filters: [],
    timeRange: recent,
    sort: [],
  },
};

/**
 * Vizzes for the specs whose result keys differ from the draft fixture's;
 * their cards answer with `biFixtureQuery` of the spec.
 */
const kVizzes: Record<string, BiVizSpec> = {
  horizontal: {
    type: BiVizType.HorizontalBar,
    x: 'name',
    series: [{ key: 'amount', name: '营收' }],
  },
  table: {
    type: BiVizType.Table,
    columns: [{ key: 'name' }, { key: 'item_count' }, { key: 'amount' }],
  },
  donut: {
    type: BiVizType.Donut,
    x: 'service_type',
    series: [{ key: 'order_count' }],
  },
};

/** Card fixtures by name, built from the draft fixtures' vizzes and results. */
function cardOf(name: string, title?: string): BiCardView {
  const draft = kBiDraftFixtures.find(entry =>
    entry.id.startsWith(`bidraft_${name}`),
  )!;
  return {
    id: `bicard_${name.padEnd(26, '0')}`,
    title: title ?? draft.title,
    spec: kSpecs[name] ?? draft.spec,
    viz: kVizzes[name] ?? draft.viz,
    sourceDraftId: draft.id,
    createdBy: 1,
    createdAt: at,
    updatedAt: at,
  };
}

export const kFailingCardName = 'failing';
export const kRestrictedCardName = 'restricted';
export const kUntimedCardName = 'untimed';

const cards = {
  kpi: cardOf('kpi'),
  line: cardOf('line'),
  horizontal: cardOf('horizontal'),
  table: cardOf('table'),
  donut: cardOf('donut'),
  stacked: cardOf('stacked'),
  blend: cardOf('blend'),
  failing: {
    ...cardOf('bar', '各服务类型施工时长'),
    id: `bicard_${kFailingCardName.padEnd(26, '0')}`,
  },
  restricted: {
    ...cardOf('pie', '毛利构成（店长可见）'),
    id: `bicard_${kRestrictedCardName.padEnd(26, '0')}`,
  },
  untimed: {
    ...cardOf('area', '车辆档案增长'),
    id: `bicard_${kUntimedCardName.padEnd(26, '0')}`,
  },
};

/** The fixture result a card answers with, by its card id. */
export function biFixtureCardResult(cardId: string): BiDraftResult | null {
  const name = cardId.slice('bicard_'.length).replace(/0+$/, '');
  const source =
    name === kFailingCardName
      ? 'bar'
      : name === kUntimedCardName
      ? 'area'
      : name;
  if (kVizzes[source]) return { current: biFixtureQuery(kSpecs[source]) };
  return (
    kBiDraftFixtures.find(entry => entry.id.startsWith(`bidraft_${source}`))
      ?.result ?? null
  );
}

function dashboard(
  id: string,
  name: string,
  list: BiCardView[],
  layout: BiDashboardLayout,
  extra: Partial<BiDashboardView> = {},
): BiDashboardView {
  return {
    id: `bidash_${id.padEnd(26, '0')}`,
    name,
    description: null,
    visibility: BiDashboardVisibility.Private,
    subjectMemberId: null,
    subjectName: null,
    subjectRole: null,
    ownerMemberId: 1,
    ownerName: '王店长',
    isDefault: false,
    revision: 3,
    permission: BiDashboardPermission.Manage,
    updatedAt: at,
    createdAt: at,
    filters: {},
    palette: BiPalette.Default,
    cards: list,
    layout,
    ...extra,
  };
}

const presetCards = [
  cards.kpi,
  cards.line,
  cards.horizontal,
  cards.failing,
  cards.table,
  cards.restricted,
  cards.untimed,
  cards.donut,
];

export function biFixtureDashboards(): BiDashboardView[] {
  return [
    dashboard(
      'preset',
      '门店经营周报',
      presetCards,
      {
        mode: BiLayoutMode.Preset,
        columns: 'auto',
        order: presetCards.map(card => card.id),
        spans: { [cards.table.id]: 'full' },
      },
      { isDefault: true },
    ),
    dashboard(
      'custom',
      '营收看板（自定义布局）',
      [cards.kpi, cards.line, cards.donut, cards.table],
      {
        mode: BiLayoutMode.Custom,
        items: [
          { cardId: cards.kpi.id, x: 0, y: 0, w: 3, h: 2 },
          { cardId: cards.donut.id, x: 0, y: 2, w: 3, h: 4 },
          { cardId: cards.line.id, x: 3, y: 0, w: 9, h: 6 },
          { cardId: cards.table.id, x: 0, y: 6, w: 12, h: 5 },
        ],
      },
      { visibility: BiDashboardVisibility.Store },
    ),
    dashboard(
      'shared',
      '技师产值',
      [cards.horizontal, cards.stacked, cards.blend],
      {
        mode: BiLayoutMode.Preset,
        columns: 'auto',
        order: [cards.horizontal.id, cards.stacked.id, cards.blend.id],
      },
      {
        ownerMemberId: 2,
        ownerName: '李主管',
        permission: BiDashboardPermission.View,
        visibility: BiDashboardVisibility.Store,
      },
    ),
  ];
}
